import { InjectionToken } from "@angular/core";
import { EstimateActionWorker } from "@bitwarden/web-vault/app/services/web-worker/estimate-actions/estimate-action.worker";
import { TransactionBalancesWorker } from "@bitwarden/web-vault/app/services/web-worker/transaction-balances/transaction-balances.worker";
import { TransactionBalancesWorkerMessage } from "@bitwarden/web-vault/app/services/web-worker/transaction-balances/transaction-balances.worker.message";
import { WebWorker } from "@bitwarden/web-vault/web-worker/web.worker";
import { WorkerMessage } from "@bitwarden/web-vault/web-worker/worker.message";
import { BalanceAlignmentWorker } from "@bitwarden/web-vault/app/services/web-worker/balance-alignment/balance-alignment.worker";
import { BalanceAlignmentWorkerMessage } from "@bitwarden/web-vault/app/services/web-worker/balance-alignment/balance-alignment.worker.message";

export const WEB_WORKER_QUEUE = new InjectionToken<WebWorkerQueue>("WEB_WORKER_QUEUE", {
  providedIn: "root",
  factory: () => WebWorkerQueue.getInstance(),
});

export class WebWorkerQueue {
  private static instance: WebWorkerQueue;

  private readonly maxWorkers = 4;
  private workers: Array<WebWorker> = [];
  private freeWorkers: Record<string, Array<WebWorker>> = {};
  private workersBusy: Array<boolean> = [];
  private queue: Array<{
    message: WorkerMessage;
    callback?: (data: any) => void;
    resolve?: (value?: any) => void;
    reject?: (reason?: any) => void;
  }> = [];

  // Private constructor to prevent direct instantiation
  private constructor() {}

  static getInstance(): WebWorkerQueue {
    if (!this.instance) {
      this.instance = new WebWorkerQueue();
    }
    return this.instance;
  }

  createNewWorker(workerType?: string): number {
    if (this.workers.length >= this.maxWorkers) {
      return;
    }
    const worker = this.getCorrectWorkerType(workerType);
    if (!worker) {
      // Unrecognized/malformed workerType — nothing to create. Bail instead
      // of dereferencing undefined, but settle every queued job of this type
      // first: callers only run a job when they get a workerIndex back, so
      // returning undefined alone would leave these sitting in the queue with
      // nothing to ever resolve/reject them. Pull them out before settling
      // so a callback that re-posts can't see them again.
      const unrunnable = this.queue.filter((job) => job.message?.workerType === workerType);
      this.queue = this.queue.filter((job) => job.message?.workerType !== workerType);
      for (const job of unrunnable) {
        this.settleJob(job, `no worker available for type "${workerType}"`);
      }
      return;
    }
    worker.workerIndex = this.workers.length;
    this.workers.push(worker);
    this.workersBusy.push(false);

    return worker.workerIndex;
  }

  getCorrectWorkerType(workerType: string): WebWorker {
    let worker: WebWorker;
    const pool = this.freeWorkers?.[workerType];
    // Skip (and terminate) any crashed instance sitting in the pool — handing
    // one a job would postMessage into the void, and onWorkerError won't fire
    // again for an error that already happened, so the job would hang.
    while (!worker && pool?.length > 0) {
      const candidate = pool.pop();
      if (candidate.crashed) {
        candidate.terminate();
      } else {
        worker = candidate;
      }
    }
    if (worker == undefined) {
      if (workerType === "EstimateActionWorker") {
        worker = EstimateActionWorker.getInstance();
      } else if (workerType === "TransactionBalancesWorker") {
        worker = TransactionBalancesWorker.getInstance();
      } else if (workerType === "BalanceAlignmentWorker") {
        worker = BalanceAlignmentWorker.getInstance();
      }
    }
    return worker;
  }

  freeWorker(worker: WebWorker) {
    worker.workerIndex = null;
    if (this.freeWorkers?.[worker.workerType]) {
      this.freeWorkers[worker.workerType].push(worker);
    } else {
      this.freeWorkers[worker.workerType] = [worker];
    }
  }

  /**
   * Settles a job that runJob() cannot actually dispatch (unmatched worker type,
   * malformed message, etc). Without this, such a job's promise/callback never
   * fires AND `workersBusy[workerIndex]` — set true at the top of runJob() —
   * never gets reset, since that reset previously only happened inside the
   * resolve/reject wrappers built for the happy path. Leak enough of these
   * (maxWorkers is only 4) and every future job silently hangs forever, since
   * getFreeWorker() never finds a free slot again. Matches the observed
   * "scenario"/"revaluation" processes that never end on a fresh account.
   */
  private settleUnhandledJob(
    workerIndex: number,
    job: (typeof this.queue)[number],
    reason: string,
  ) {
    this.workersBusy[workerIndex] = false;
    this.settleJob(job, reason);
    this.processQueue();
  }

  /**
   * Rejects (or error-callbacks) a job that will never be dispatched. Does not
   * touch worker slots — see settleUnhandledJob() for the slot-owning variant.
   */
  private settleJob(job: (typeof this.queue)[number], reason: string) {
    const error = new Error(`WebWorkerQueue: ${reason}`);
    if (job.reject) {
      job.reject(error);
    } else if (job.callback) {
      // ScenarioOption.returnFromEstimateAction (the only callback-style
      // consumer) reads the error off `message.data.error`, not
      // `message.error` — nest it there too or this failure gets silently
      // treated as a success with default permutation/estimate indices.
      // permutationIndex/estimateIndex/estimateActions must be forwarded
      // from the original job message too — without them,
      // returnFromEstimateAction defaults to 0/[] and silently marks an
      // incomplete permutation as done instead of correctly advancing it.
      // createdRecords must be forwarded too — otherwise returnFromEstimateAction
      // defaults it to {}, wiping the runningAccountBalances accumulated so far
      // and corrupting the rest of this permutation instead of just failing
      // this one action.
      const jobData = (job.message as { data?: any })?.data ?? {};
      job.callback({
        error,
        data: {
          error,
          permutationIndex: jobData.permutationIndex,
          estimateIndex: jobData.estimateIndex,
          estimateActions: jobData.estimateActions,
          createdRecords: jobData.createdRecords,
        },
      });
    }
  }

  async runJob(workerIndex: number) {
    this.workersBusy[workerIndex] = true;
    let worker: WebWorker | undefined = this.workers[workerIndex];
    if (this.queue.length > 0) {
      const job = this.queue.shift();
      if (job && job?.message?.id && job?.message?.workerType) {
        // check that the worker is the correct type for the job, or that the
        // worker previously assigned to this slot didn't crash on its last
        // job — a crashed worker keeps failing identically, so it must be
        // replaced rather than handed the next job.
        if (!worker || worker.crashed || worker.workerType !== job.message.workerType) {
          const replacement = this.getCorrectWorkerType(job.message.workerType);
          if (replacement) {
            if (worker) {
              // A crashed worker must never go back into the free pool for
              // reuse elsewhere — terminate it instead of freeWorker()'ing
              // it, or the next job of any type could be handed the same
              // dead instance and fail the same way. A worker that's only
              // the wrong type is still healthy, so it goes back to the
              // free pool as before.
              if (worker.crashed) {
                worker.terminate();
              } else {
                this.freeWorker(worker);
              }
            }
            replacement.workerIndex = workerIndex;
            this.workers[workerIndex] = replacement;
            worker = replacement;
          } else {
            // No worker available for this type — leave `this.workers[workerIndex]`
            // untouched (don't free the current worker or overwrite the slot
            // with undefined) so this slot isn't permanently poisoned for the
            // *next* job. Only the local `worker` is cleared, so no branch
            // below matches and the job falls through to settleUnhandledJob()
            // instead of throwing.
            worker = undefined;
          }
        }
        if (worker instanceof EstimateActionWorker) {
          try {
            job.message.data.workerIndex = workerIndex;
            await worker.runEstimateAction(job.message, (data: any) => {
              const returnedWorkerIndex = data?.data?.workerIndex;
              this.workersBusy[returnedWorkerIndex] = false;
              this.processQueue();
              // Settle through whichever handler the job has: a postMessagePromise job has no callback.
              (job.callback ?? job.resolve)?.(data);
            });
          } catch (error) {
            // Malformed data, or DataCloneError from postMessage (runEstimateAction
            // posts before wiring worker.callback, so nothing is left attached).
            this.settleUnhandledJob(workerIndex, job, `estimate action dispatch failed: ${error}`);
          }
        } else if (
          worker instanceof TransactionBalancesWorker ||
          worker instanceof BalanceAlignmentWorker
        ) {
          // Always attached, so a job frees its slot even when nothing is waiting on it. A callback
          // job settles both ways through its callback. `settle?.` because a postMessage job may
          // have no callback; throwing here would skip processQueue() and stall the queue.
          const finish = (settle: (data: any) => void) => (data: any) => {
            this.workersBusy[workerIndex] = false;
            settle?.(data);
            this.processQueue();
          };
          worker.resolve = finish(job.resolve ?? job.callback);
          worker.reject = finish(job.reject ?? job.callback);
          try {
            if (
              worker instanceof TransactionBalancesWorker &&
              job.message instanceof TransactionBalancesWorkerMessage
            ) {
              job.message.workerIndex = workerIndex;
              await worker.getBalanceForTransactions(job.message);
            } else if (
              worker instanceof BalanceAlignmentWorker &&
              job.message instanceof BalanceAlignmentWorkerMessage
            ) {
              job.message.workerIndex = workerIndex;
              await worker.realignTransactions(job.message);
            } else {
              throw new Error(`message type did not match worker type "${job.message.workerType}"`);
            }
          } catch (error) {
            // The post failed (e.g. DataCloneError) or the message didn't match the worker type.
            // Detach the wrappers before settling: this worker can later sit in the free pool,
            // and an "error" event there would fire them and free a slot another job owns.
            // Must happen before settleUnhandledJob, whose processQueue() can hand this
            // worker to the next job straight away.
            worker.resolve = null;
            worker.reject = null;
            this.settleUnhandledJob(workerIndex, job, `balance dispatch failed: ${error}`);
          }
        } else {
          // getCorrectWorkerType() returned undefined or an unrecognized worker
          // instance — no branch above can run this job.
          this.settleUnhandledJob(
            workerIndex,
            job,
            `no worker available for type "${job.message.workerType}"`,
          );
        }
      } else if (job) {
        // Malformed job (missing message id/workerType) — still occupies a
        // queue slot and must not silently swallow the caller's promise.
        this.settleUnhandledJob(workerIndex, job, "job missing message id/workerType");
      } else {
        // Nothing to run — this worker slot must not stay marked busy forever.
        this.workersBusy[workerIndex] = false;
      }
    } else {
      this.workersBusy[workerIndex] = false;
    }
  }

  getFreeWorker(workerType: string): number {
    for (let i = 0; i < this.workersBusy.length; i++) {
      if (!this.workersBusy[i]) {
        return i;
      }
    }
    if (this.workers.length < this.maxWorkers) {
      return this.createNewWorker(workerType);
    }
  }

  async processQueue() {
    if (this.queue.length > 0) {
      const workerType = this.queue[0].message.workerType;
      const workerIndex = this.getFreeWorker(workerType);
      if (workerIndex !== undefined) {
        await this.runJob(workerIndex);
      }
    }
  }

  async postMessage(message: WorkerMessage, callback?: (data: any) => void) {
    this.queue.push({ message: message, callback: callback });

    const workerIndex = this.getFreeWorker(message.workerType);
    if (workerIndex !== undefined) {
      await this.runJob(workerIndex);
    }
  }

  async postMessagePromise(message: WorkerMessage): Promise<WorkerMessage> {
    const promise: Promise<WorkerMessage> = new Promise((resolve, reject) => {
      this.queue.push({ message: message, resolve: resolve, reject: reject });
    });

    const workerIndex = this.getFreeWorker(message.workerType);
    if (workerIndex !== undefined) {
      await this.runJob(workerIndex);
    }
    return promise;
  }
}
